import React, { useMemo } from 'react'
import { Icon } from 'components'
import styles from './segmentedProgressSteps.module.scss'

const GAP_PX = 4
const SELECTED_SCALE = 1.1

const DEFAULT_COLORS_3: string[] = [
  'rgb(240, 66, 66)',
  'rgb(231, 176, 8)',
  'rgb(33, 196, 93)',
]
const DEFAULT_COLORS_4: string[] = [
  'rgb(240, 66, 66)',
  'rgb(249, 116, 21)',
  'rgb(231, 176, 8)',
  'rgb(33, 196, 93)',
]

function getDefaultColors(blockCount: number): string[] {
  if (blockCount === 3) return DEFAULT_COLORS_3
  if (blockCount === 4) return DEFAULT_COLORS_4
  return Array(blockCount).fill('transparent')
}

/** Range [min, max] (inclusive) for each block. Value falls into one range to determine selected block. */
export type BlockRange = [number, number]

interface SegmentedProgressStepsProps {
  /** Optional className for the root element */
  className?: string
  /** Width of each block in px (default 48) */
  blockWidth?: number
  /** Height of each block in px (default 32) */
  blockHeight?: number
  /** Current value – used with ranges to determine which block is selected */
  value: number
  /** Range [min, max] per block (inclusive). Length = number of blocks. */
  ranges: BlockRange[]
  /** Background color per block. Optional: defaults to 3 or 4 preset colors when ranges length is 3 or 4. Non-selected blocks use same color at 0.3 opacity. */
  colors?: string[]
}

function getSelectedIndex(value: number, ranges: BlockRange[]): number {
  const index = ranges.findIndex(([min, max]) => value >= min && value <= max)
  if (index >= 0) return index
  if (value < ranges[0][0]) return 0
  return ranges.length - 1
}

function getBlockIcon(index: number, total: number): { icon: string; size?: number } {
  if (index === 0) return { icon: 'Close', size: 16 }
  if (index === total - 1) return { icon: 'Check', size: 16 }
  return { icon: 'Lens', size: 7 }
}

/**
 * Capsule-shaped segmented progress indicator.
 * First block shows X, middle blocks show a dot, last block shows check.
 * Gap between blocks is 4px. Selected block is scaled 10% (no layout change).
 */
const SegmentedProgressSteps: React.FC<SegmentedProgressStepsProps> = (props) => {
  const {
    className,
    blockWidth = 48,
    blockHeight = 32,
    value,
    ranges,
    colors,
  } = props

  const blockCount = ranges.length
  const effectiveColors =
    colors && colors.length === blockCount ? colors : getDefaultColors(blockCount)

  const selectedIndex = useMemo(() => getSelectedIndex(value, ranges), [value, ranges])
  const totalWidth = blockCount * blockWidth + (blockCount - 1) * GAP_PX
  const borderRadius = blockHeight / 2

  return (
    <div
      className={`${styles.root} ${className ?? ''}`.trim()}
      style={{
        width: totalWidth,
        height: blockHeight,
      }}
      role="img"
      aria-label={`Progress: step ${selectedIndex + 1} of ${blockCount}`}
      data-testid="segmented-progress-steps"
    >
      {ranges.map((_, index) => {
        const isFirst = index === 0
        const isLast = index === blockCount - 1
        const isSelected = index === selectedIndex
        const { icon, size = 16 } = getBlockIcon(index, blockCount)

        return (
          <div
            key={index}
            className={`${styles.segment} ${isSelected ? styles.segmentSelected : styles.segmentOff}`}
            data-testid={`segmented-progress-segment-${index}${isSelected ? '-selected' : ''}`}
            style={{
              width: blockWidth,
              height: blockHeight,
              backgroundColor: effectiveColors[index] ?? 'transparent',
              opacity: isSelected ? 1 : 0.3,
              borderRadius: isFirst
                ? `${borderRadius}px 0 0 ${borderRadius}px`
                : isLast
                  ? `0 ${borderRadius}px ${borderRadius}px 0`
                  : 0,
              ...(isSelected
                ? {
                    transform: `scale(${SELECTED_SCALE})`,
                    transformOrigin: 'center center',
                  }
                : {}),
            }}
          >
            {isSelected && (
              <span className={styles.blockIcon}>
                <Icon materialIcon={icon} color="#fff" size={size} />
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default SegmentedProgressSteps
